import { useRef, useEffect } from "react";
import { useCesiumContext } from "../cesium/CesiumContext";
import {
    Cartesian3,
    Entity,
    JulianDate,
    Matrix3,
    Matrix4,
} from "@cesium/engine";
import { isDefined } from "../../lib/utils";
import { 
    setFPVEnabled
} from "../../slices/firstPersonView";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useClockManager, useClockOnTick } from "../clock";
import { getTimelineWidth, visibilityImageTimeSampleBarHeight } from "./VisibilityInfo";
import { MODEL_OFFSETS } from "src/constants";
import { hideVisibilityBar } from "src/slices/visibilityBarState";
import { Dialog, DialogContent, DialogTitle} from "../base/Dialog";
import { Button } from "../base/Button";
import Tooltip from "../base/Tooltip";
import { ChartLine, ScanIcon } from "lucide-react";
import { VisuallyHidden } from "react-aria";

interface VisibilityBarProps {
    imageUrl: string;
    startTime?: JulianDate;
    stopTime?: JulianDate;
    cameraPosition: Cartesian3;
    onVisibilityInfoClosure: () => void;
    trackingViewers: [string | undefined, string][];
    entityOffsets?: [Cartesian3];
    trackedEntity?: Entity;
}

export default function VisibilityBarWrapper() {
    const {
        visible,
        imageUrl,
        startTime,
        stopTime,
        cameraPosition,
        trackingViewers,
        entityOffsets,
        trackedEntity,
        openVisibilityPlot,
      } = useAppSelector((state) => state.visibilityBarState);

    const { map: viewer } = useCesiumContext();
    const dispatch = useAppDispatch();
    
    const shouldRender =
        visible &&
        imageUrl &&
        cameraPosition &&
        viewer &&
        !viewer.isDestroyed();
    
    const onVisibilityInfoClosure = () => {
        dispatch(hideVisibilityBar());
    };
    
    let isTraverse = false;
    if (isDefined(trackedEntity) && isDefined(trackedEntity.position)) {
      isTraverse = true;
    }

    useEffect(() => {
        return () => {
            onVisibilityInfoClosure();
        };
      }, []);

    let content: string;
    if (isTraverse) {
        content = "along traverse";
    } else {
        content = "at static position";
    }

    if (!shouldRender) {
        return null;
    }

    return (
        <div>
            <Dialog
                modal={false}
                open={visible}
                onOpenChange={(open) => {
                if (!open) {
                    onVisibilityInfoClosure();
                }
                }}
            >
                <VisuallyHidden>
                    <DialogTitle>Showing visibility info {content}</DialogTitle>
                </VisuallyHidden>
                <DialogContent
                className="top-10 translate-y-0 p-3 w-[350px]"
                onOpenAutoFocus={preventDefault}
                onCloseAutoFocus={preventDefault}
                onPointerDownOutside={preventDefault}
                onInteractOutside={preventDefault}
                >
                <h3>Showing visibility info {content}</h3>
                <div className="flex flex-row justify-center items-center">
                {isDefined(cameraPosition) &&
                    !cameraPosition.equals(Cartesian3.ZERO) && (
                    
                        <Tooltip content="Go to initial position">
                            <Button
                                size="md"
                                variant="secondary"
                                isQuiet
                                onPress={() => {
                                    let orientation;
                                    if (viewer.trackedEntity) {
                                        // horizon looking in local frame
                                        const dirL = new Cartesian3(1, 0, 0);
                                        const upL = new Cartesian3(0, 0, 1);
                                        // rotation matrix
                                        const R = new Matrix3();
                                        Matrix4.getRotation(viewer.camera.transform, R);
                                        // convert orientation to global frame
                                        const dir = new Cartesian3();
                                        const up = new Cartesian3();
                                        Matrix3.multiplyByVector(R, dirL, dir);
                                        Matrix3.multiplyByVector(R, upL, up);
                                        orientation = {
                                            direction: dir,
                                            up: up,
                                        }
                                    }
                                    viewer.cameraFlyTo({
                                        destination: viewer.adjustCartesian(cameraPosition),
                                        orientation
                                    });
                                }}
                            >
                                <ScanIcon />
                            </Button>
                        </Tooltip>
                )}
                {openVisibilityPlot && (
                    <Tooltip content="Open Visibility Plot">
                        <Button
                            size="sm"
                            variant="secondary"
                            isQuiet
                            onPress={openVisibilityPlot}
                        >
                            <ChartLine />
                        </Button>
                    </Tooltip>
                )}
                </div>
            </DialogContent>
            </Dialog>
            <VisibilityBar
                imageUrl={imageUrl}
                startTime={startTime}
                stopTime={stopTime}
                cameraPosition={cameraPosition}
                onVisibilityInfoClosure={onVisibilityInfoClosure}
                trackingViewers={trackingViewers}
                trackedEntity={trackedEntity}
                entityOffsets={entityOffsets}
            ></VisibilityBar>;
        </div>
    );
}

function VisibilityBar({imageUrl, startTime, stopTime, cameraPosition, onVisibilityInfoClosure, trackingViewers, entityOffsets, trackedEntity}: VisibilityBarProps) {
    const { map } = useCesiumContext();
    const imageRef = useRef<HTMLImageElement>(null);
    const selectedEntity = useRef<{name: string;}>(undefined);
    const waitingForTransformUpdate = useRef(false);
    const dispatch = useAppDispatch();
    const clockManager = useClockManager();
    
    useClockOnTick(() => {
        // resize image if needed
        if (imageRef.current && isDefined(startTime) && isDefined(stopTime)) {
            const timeLineWidth = getTimelineWidth(map, clockManager);
            if (timeLineWidth && timeLineWidth !== imageRef.current.offsetWidth) {
                const currentHeight = imageRef.current.offsetHeight;
                imageRef.current.style.width = `${timeLineWidth}px`;
                imageRef.current.style.height = `${currentHeight}px`;
            }
        }
    }, 1000);

    const processMouseMove = (event: MouseEvent) => {
        if (imageRef.current && isDefined(startTime) && isDefined(stopTime)) {
            const imageHeight = imageRef.current.offsetHeight - visibilityImageTimeSampleBarHeight();
            const imageWidth = imageRef.current.offsetWidth;
            
            // find the mouse x,y position relative to the image
            const target = event.target as HTMLImageElement;
            const br = target.getBoundingClientRect();
            const x = event.pageX - br.left;
            const y = event.pageY - br.top;
            if (x < 0 || x > br.width ||
                y < 0 || y > br.height - visibilityImageTimeSampleBarHeight()) {
                // outside the image
                return;
            }

            // number of seconds between two pixels in the time bar
            const deltaTime = JulianDate.secondsDifference(stopTime, startTime);
            const tickStep = deltaTime / imageWidth;
            const secsFromStartTime = tickStep * x; // secs
            const newTime = new JulianDate();
            JulianDate.addSeconds(startTime, secsFromStartTime, newTime);
            map.clock.currentTime = newTime;
            let trackingViewerIdx = -1;
            // select the body only on mouse down event
            if (!isDefined(selectedEntity.current)) {
                // get number of entities
                const nBodies = map.qtsLight?.lightSources.length ?? 0;
                const nTrackedEntities = trackingViewers?.length ?? 0;
                const nVisEntities = nBodies + nTrackedEntities;
                const visibilityImageItemHeight = imageHeight / nVisEntities;
                const entityIdx = Math.floor(y / visibilityImageItemHeight);
                if (entityIdx < nBodies) {
                    const bodyName = map.qtsLight?.lightSources[entityIdx];
                    if (isDefined(bodyName)) {
                        selectedEntity.current = {
                            name: bodyName,
                        }
                    }
                } else {
                    trackingViewerIdx = entityIdx - nBodies;
                    const entityId = trackingViewers[trackingViewerIdx];
                    if (isDefined(entityId)) {
                        selectedEntity.current = {
                            name: entityId.join(","),
                        }
                    }
                }
            }
            if (isDefined(selectedEntity.current)) {
                if (isDefined(trackedEntity)) {
                    if(trackedEntity !== map.trackedEntity) {
                        map.trackedEntity = trackedEntity;
                    }
                    dispatch(setFPVEnabled(true));
                }
                let cameraPositionL = Cartesian3.clone(map.adjustCartesian(cameraPosition));
                if (trackedEntity && !Matrix4.equals(map.camera.transform, Matrix4.IDENTITY)) {
                    cameraPositionL = new Cartesian3(0, 0, 0.1);
                }
                Cartesian3.clone(cameraPositionL, map.camera.position);
                
                let entityPos;
                if (map.qtsLight?.lightSources.includes(selectedEntity.current?.name)) {
                    entityPos = map.qtsLight?.getBodySampledPosition?.(selectedEntity.current?.name);
                } else {
                    const entityId = selectedEntity.current?.name.split(",");
                    const entity = map.getEntity({datasourceId: entityId[0], entityId: entityId[1]});
                    if (!entity) return;
                    let entityOffset;
                    if (isDefined(entityOffsets) && trackingViewerIdx >= 0)
                        entityOffset = entityOffsets[trackingViewerIdx];
                    else {
                        const modelUri = entity.model?.uri?.getValue(map.clock.currentTime);
                        entityOffset = MODEL_OFFSETS[modelUri] ?? Cartesian3.ZERO;
                    }
                    entityPos = map.getEntityPosition(entity, map.clock.currentTime, entityOffset);
                }
                if (!isDefined(entityPos)) {
                    return;
                }
                // Delay adding `onPostRender` until the next frame. This is important
                // to finalize FPV camera update
                setTimeout(() => {
                    const onPostRender = () => {
                        // adjust camera position AFTER the camera transform has been updated
                        map.lookAtPos(entityPos);
                        map.scene.postRender.removeEventListener(onPostRender);
                    };
                    map.scene.postRender.addEventListener(onPostRender);
                }, 0); // `setTimeout` with 0 ensures this runs after the current event loop
            }
        }
    };
    const handleMouseMove = (event: MouseEvent) => {
        if (waitingForTransformUpdate.current) {
            // Wait for the transform update
            map.scene.postRender.addEventListener(function waitForUpdate() {
                waitingForTransformUpdate.current = false;
                map.scene.postRender.removeEventListener(waitForUpdate);
                 // Perform logic after transform update
                processMouseMove(event);
            });
        } else {
            processMouseMove(event);
        }
    }
    const handleMouseUp = (event: MouseEvent) => {
        if (event.button === 0) {
            selectedEntity.current = undefined;
            // Remove global event listeners to clean up
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('mousemove', handleMouseMove);
        }
    }
    const handleMouseDown = (event: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
        if (event.button === 0) {
            if (isDefined(trackedEntity)) {
                if(trackedEntity !== map.trackedEntity) {
                    map.trackedEntity = trackedEntity;
                    waitingForTransformUpdate.current = true;
                }
                dispatch(setFPVEnabled(true));
            }
            
            // Add global event listeners
            document.addEventListener('mouseup', handleMouseUp);
            document.addEventListener('mousemove', handleMouseMove);
            handleMouseMove(event.nativeEvent);
        }
    }
    useEffect(() => {
        return () => {
          // Ensuring all global events are cleaned up when the component unmounts
          document.removeEventListener('mouseup', handleMouseUp);
          document.removeEventListener('mousemove', handleMouseMove);
        };
      }, []);
    
    return (
        <div>
        
        <div className="fixed bottom-2 right-0 z-50 cursor-pointer">
          <img 
            src={imageUrl}
            ref={imageRef}
            onMouseDown={handleMouseDown}
            draggable="false"/>
            <button
                onClick={() => onVisibilityInfoClosure()}
                className="absolute right-1 top-0 rounded-full text-sm">
                ✕
            </button>
        </div>
        </div>
      );
}

function preventDefault(evt: Event) {
    evt.preventDefault();
  }
  